import React, { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../services/apiClient';
import { ArrowPathIcon } from '../../components/Icons';
import { DataListTable, type Column } from '../../components/crm/DataListTable';

interface AuditRow {
  id: string;
  user_email: string | null;
  action: string;
  platform_action?: string;
  object_type: string;
  object_id: string | null;
  object_name: string;
  timestamp: string | null;
  metadata: Record<string, unknown>;
}

const ACTION_LABELS: Record<string, string> = {
  access_request_approved: 'Access request approved',
  access_request_denied: 'Access request denied',
  tenant_suspend: 'Tenant suspended',
  tenant_reactivate: 'Tenant reactivated',
  tenant_soft_delete: 'Tenant soft deleted',
  tenant_restore: 'Tenant restored',
  impersonation_started: 'Impersonation started',
  impersonation_ended: 'Impersonation ended',
};

const getActionLabel = (platformAction?: string, action?: string) => {
  if (platformAction && ACTION_LABELS[platformAction]) {
    return ACTION_LABELS[platformAction];
  }
  return platformAction || action || '-';
};

const AuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const data = await apiClient.get('/platform/admin/audit-logs/', { page: String(page) });
      setLogs(data.results ?? []);
      setTotalPages(data.total_pages ?? 1);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const columns = useMemo<Column<AuditRow>[]>(
    () => [
      {
        id: 'timestamp',
        header: 'Timestamp',
        mobilePriority: 'primary',
        mobileLabel: 'Timestamp',
        render: (l) => (
          <span className="text-sm text-fg-muted">
            {l.timestamp ? new Date(l.timestamp).toLocaleString() : '-'}
          </span>
        ),
      },
      {
        id: 'user',
        header: 'User',
        mobileLabel: 'User',
        render: (l) => <span className="text-sm text-fg-muted">{l.user_email || '-'}</span>,
      },
      {
        id: 'action',
        header: 'Action',
        mobileLabel: 'Action',
        render: (l) => (
          <span className="text-sm text-white">{getActionLabel(l.platform_action, l.action)}</span>
        ),
      },
      {
        id: 'target',
        header: 'Target',
        mobileLabel: 'Target',
        render: (l) => (
          <span className="text-sm text-fg-muted">
            {l.object_type}
            {l.object_name ? `: ${l.object_name}` : ''}
          </span>
        ),
      },
      {
        id: 'details',
        header: 'Details',
        mobileLabel: 'Details',
        cellClassName: 'max-w-xs truncate whitespace-normal',
        render: (l) => (
          <span className="text-sm text-fg-muted">
            {Object.keys(l.metadata || {}).length > 0 ? JSON.stringify(l.metadata) : '-'}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Platform Audit Logs</h1>
          <p className="text-fg-muted text-sm mt-1">
            Superuser actions only. Tenant-scoped audit logs are visible within tenant context.
          </p>
        </div>
        <button
          onClick={() => fetchLogs()}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-fg-secondary bg-surface-muted border border-border rounded-lg hover:bg-surface-muted flex items-center gap-2 disabled:opacity-50"
        >
          <ArrowPathIcon size={4} className={loading ? 'animate-spin' : undefined} />
          Refresh
        </button>
      </div>
      <DataListTable<AuditRow>
        columns={columns}
        rows={logs}
        getRowKey={(l) => l.id}
        loading={loading}
        emptyContent={<div className="text-fg-muted p-4">No audit logs found.</div>}
      />
      {!loading && totalPages > 1 && (
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-4 py-2 rounded bg-surface-muted text-white disabled:opacity-50"
              >
                Previous
              </button>
              <span className="px-4 py-2 text-fg-muted">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-4 py-2 rounded bg-surface-muted text-white disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
    </div>
  );
};

export default AuditLogs;
